import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * Mini set d'icônes outline suffisant pour la sidebar/topbar/UI de LEM.
 * SVG inline maison (pas de dépendance externe type lucide-react, non
 * disponible côté Angular). Les chemins sont des constantes internes
 * (jamais issues d'une saisie utilisateur) : le bypass de sanitisation
 * est donc sûr ici.
 */
export type IconName =
  | 'dashboard'
  | 'cart'
  | 'invoice'
  | 'box'
  | 'users'
  | 'truck'
  | 'book'
  | 'settings'
  | 'chevron-down'
  | 'chevron-right'
  | 'search'
  | 'bell'
  | 'plus'
  | 'wifi'
  | 'logout'
  | 'menu'
  | 'close'
  | 'building'
  | 'shield'
  | 'percent'
  | 'plug'
  | 'credit-card'
  | 'smartphone'
  | 'clock'
  | 'history'
  | 'monitor'
  | 'tag'
  | 'clipboard-list'
  | 'swap'
  | 'warehouse'
  | 'file-text'
  | 'folder'
  | 'wallet'
  | 'rotate-ccw'
  | 'trending-up'
  | 'alert-triangle'
  | 'eye'
  | 'download'
  | 'send'
  | 'more-horizontal'
  | 'trash'
  | 'edit'
  | 'check'
  | 'save'
  | 'circle-x'
  | 'lock';

const PATHS: Record<IconName, string> = {
  dashboard: '<rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>',
  cart: '<circle cx="9" cy="20" r="1.5"/><circle cx="18" cy="20" r="1.5"/><path d="M2 3h2l2.6 12.4a2 2 0 0 0 2 1.6h8.8a2 2 0 0 0 2-1.6L21 8H6"/>',
  invoice: '<path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Z"/><path d="M15 2v5h5"/><path d="M8 12h8M8 16h5"/>',
  box: '<path d="M21 8 12 3 3 8l9 5 9-5Z"/><path d="M3 8v9l9 5 9-5V8"/><path d="M12 13v9"/>',
  users: '<circle cx="9" cy="8" r="3.2"/><path d="M2.5 19c0-3.3 2.9-6 6.5-6s6.5 2.7 6.5 6"/><path d="M17 8.2a3 3 0 1 1 0 6"/><path d="M21.5 19c0-2.6-1.9-4.8-4.5-5.6"/>',
  truck: '<rect x="1.5" y="6" width="13" height="10" rx="1.2"/><path d="M14.5 10h4l3 3.2V16h-7z"/><circle cx="6" cy="18.5" r="1.6"/><circle cx="17.5" cy="18.5" r="1.6"/>',
  book: '<path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v17H6.5A2.5 2.5 0 0 0 4 21.5Z"/><path d="M4 4.5v15"/>',
  settings: '<circle cx="12" cy="12" r="3.2"/><path d="M19.4 13.8a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.04 1.56V20a2 2 0 1 1-4 0v-.09A1.7 1.7 0 0 0 9 18.35a1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .34-1.87A1.7 1.7 0 0 0 3.09 13H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.65 8a1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 3.65a1.7 1.7 0 0 0 1.04-1.56V2a2 2 0 1 1 4 0v.09A1.7 1.7 0 0 0 15 3.65a1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.35 9c.12.02.24.03.35.05H20a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.51 1.75Z"/>',
  'chevron-down': '<path d="m6 9 6 6 6-6"/>',
  'chevron-right': '<path d="m9 6 6 6-6 6"/>',
  search: '<circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/>',
  bell: '<path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.7 21a2 2 0 0 1-3.4 0"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  wifi: '<path d="M2 8.5a16 16 0 0 1 20 0"/><path d="M5.5 12a11 11 0 0 1 13 0"/><path d="M9 15.5a6 6 0 0 1 6 0"/><circle cx="12" cy="19" r="1"/>',
  logout: '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="M16 17l5-5-5-5"/><path d="M21 12H9"/>',
  menu: '<path d="M3 6h18M3 12h18M3 18h18"/>',
  close: '<path d="M18 6 6 18M6 6l12 12"/>',
  building: '<rect x="4" y="2" width="16" height="20" rx="1"/><path d="M9 6h1M14 6h1M9 10h1M14 10h1M9 14h1M14 14h1M9 18h6"/>',
  shield: '<path d="M12 2 4 5v6c0 5 3.4 8.7 8 11 4.6-2.3 8-6 8-11V5Z"/>',
  percent: '<path d="M19 5 5 19"/><circle cx="6.5" cy="6.5" r="2.2"/><circle cx="17.5" cy="17.5" r="2.2"/>',
  plug: '<path d="M7 7 3 3M17 7l4-4M8 8l8 8M4 12a8 8 0 0 0 8 8l4-4-8-8-4 4Z"/>',
  'credit-card': '<rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/>',
  smartphone: '<rect x="6" y="2" width="12" height="20" rx="2"/><path d="M10 5h4M11 18h2"/>',
  clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/>',
  history: '<path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/><path d="M12 7v5l3.5 2"/>',
  monitor: '<rect x="2" y="4" width="20" height="13" rx="2"/><path d="M8 21h8M12 17v4"/>',
  tag: '<path d="M20.6 12.1 12 20.7a2 2 0 0 1-2.8 0l-7-7a2 2 0 0 1 0-2.8L10.8 2.3a2 2 0 0 1 1.4-.6H19a2 2 0 0 1 2 2v6.9a2 2 0 0 1-.4 1.5Z"/><circle cx="15.5" cy="8.5" r="1.5"/>',
  'clipboard-list': '<rect x="5" y="4" width="14" height="17" rx="2"/><path d="M9 2h6v4H9z"/><path d="M9 11h6M9 15h6M9 19h3"/>',
  swap: '<path d="m17 2 4 4-4 4"/><path d="M3 6h18"/><path d="m7 22-4-4 4-4"/><path d="M21 18H3"/>',
  warehouse: '<path d="M22 8.35V21H2V8.35a1 1 0 0 1 .6-.92l9-4.5a1 1 0 0 1 .8 0l9 4.5a1 1 0 0 1 .6.92Z"/><path d="M6 21v-7h12v7"/>',
  'file-text': '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h6"/>',
  folder: '<path d="M3 6a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v9a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3Z"/><path d="M3 9h18"/>',
  wallet: '<path d="M20 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2Z"/><path d="M16 4H8a2 2 0 0 0-2 2v1h12V6a2 2 0 0 0-2-2Z"/><circle cx="16.5" cy="13.5" r="1.2"/>',
  'rotate-ccw': '<path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/>',
  'trending-up': '<path d="m3 17 6-6 4 4 8-8"/><path d="M17 7h4v4"/>',
  'alert-triangle': '<path d="M10.3 3.6 1.8 18a1.7 1.7 0 0 0 1.5 2.5h17.4a1.7 1.7 0 0 0 1.5-2.5L13.7 3.6a1.7 1.7 0 0 0-3 0Z"/><path d="M12 9v4M12 17h.01"/>',
  eye: '<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/>',
  download: '<path d="M12 3v12m0 0 4-4m-4 4-4-4"/><path d="M4 19h16"/>',
  send: '<path d="m3 3 18 9-18 9 4-9Z"/>',
  'more-horizontal': '<circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/>',
  trash: '<path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13"/>',
  edit: '<path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/>',
  check: '<path d="M20 6 9 17l-5-5"/>',
  save: '<path d="M5 3h12l2 2v16H5z"/><path d="M8 3v6h8V3M8 21v-7h8v7"/>',
  'circle-x': '<circle cx="12" cy="12" r="9"/><path d="m9 9 6 6M15 9l-6 6"/>',
  lock: '<rect x="5" y="10" width="14" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/>',
};

@Component({
  selector: 'lem-icon',
  standalone: true,
  imports: [CommonModule],
  template: `<svg
    [attr.width]="size"
    [attr.height]="size"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    [attr.stroke-width]="strokeWidth"
    stroke-linecap="round"
    stroke-linejoin="round"
    [innerHTML]="path"
    aria-hidden="true"
  ></svg>`,
})
export class IconComponent {
  @Input({ required: true }) name!: IconName;
  @Input() size = 20;
  @Input() strokeWidth = 1.8;

  private readonly cache = new Map<IconName, SafeHtml>();

  constructor(private readonly sanitizer: DomSanitizer) {}

  get path(): SafeHtml {
    if (!this.cache.has(this.name)) {
      this.cache.set(this.name, this.sanitizer.bypassSecurityTrustHtml(PATHS[this.name] ?? ''));
    }
    return this.cache.get(this.name)!;
  }
}
